/* eslint no-unused-vars: 0 */

/**
 * Represents the base class for components.
 */
class Component extends HTMLElement {
  /**
   * Gets the component id.
   */
  get id() {
    return this.getAttribute('id');
  }

  /**
   * Sets the component id.
   */
  set id(value) {
    this.setAttribute('id', value);
  }

  /**
   * Gets the name of the component.
   */
  get name() {
    return this.getAttribute('name');
  }

  /**
   * Sets the name of the component.
   */
  set name(value) {
    this.setAttribute('name', value);
  }

  /**
   * Gets the title of the component.
   */
  get title() {
    return this.getAttribute('title');
  }

  /**
   * Sets the title of the component.
   */
  set title(value) {
    this.setAttribute('title', value);
  }

  /**
   * Adds an attribute as a string.
   * @param {String} name The attribute name.
   * @param {String} value The attribute value.
   * @param {Boolean} valueRequired A value that determines whether the value is required for the attribute.
   * @returns String
   */
  addAttributeMarkup(name, value, valueRequired = true) {
    return value && valueRequired ? ` ${name}="${value}"` : !valueRequired ? ` ${name}` : '';
  }

  /**
   * Called every time an observed attribute changes.
   * @param {String} name The property that changed.
   * @param {String} oldValue The old value.
   * @param {String} newValue The new value.
   */
  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue !== newValue) {
      this.onAttributeChange(name, oldValue, newValue);
      this.render();
    }
  }

  /**
   * Creates a template for use with components.
   * @returns HTMLElement
   */
  createTemplate() {
    const template = document.createElement('template');
    template.innerHTML = `
      <style>
        @import url('${process.env.APP_CSS_PATH}');
      </style>
    `;

    return template;
  }

  /**
   * Fires a custom event for the component.
   * @param {String} name The event name.
   * @param {Any} detail The event data.
   */
  fireCustomEvent(name, detail = null) {
    if (name) {
      const event = new CustomEvent(name, { detail });

      this.dispatchEvent(event);
    }
  }

  /**
   * Initializes the shadow root for the component.
   * @param {Node} node The HTML node to initialize the shaddow root for.
   * @param {Boolean} delegatesFocus A value that determines whether focus is passed down to a child element.
   */
  initShadowRoot(node, delegatesFocus = false) {
    let shadowRoot = this.shadowRoot;

    if (!shadowRoot) {
      shadowRoot = this.attachShadow({ mode: 'open', delegatesFocus: delegatesFocus });
    }

    shadowRoot.appendChild(node);
  }

  /**
   * Provides template initialization.
   * @abstract
   */
  initTemplate() {
    throw new Error('initTemplate must be implemented by subclass.');
  }

  /**
   * Called when the component has been added to the DOM.
   */
  connectedCallback() {
    this.initTemplate();
    this.onMounted();
    this.render();
  }

  /**
   * Called when the component has been removed from the DOM.
   */
  disconnectedCallback() {
    this.onUnmounted();
  }

  /**
   * Occurs when an attribute changes in a subclass.
   * @param {String} name The property that changed.
   * @param {String} oldValue The old value.
   * @param {String} newValue The new value.
   */
  onAttributeChange(name, oldValue, newValue) {}

  /**
   * Occurs when the component has mounted to the DOM.
   */
  onMounted() {}

  /**
   * Occurs when the component has been unmounted from the DOM.
   */
  onUnmounted() {}

  /**
   * Renders the component.
   * @abstract
   */
  render() {}
}

export default Component;
